import { useMemo } from 'react';

/**
 * Custom hook to check user permissions
 * @param {Object} user - User object with permissions array
 * @returns {Object} Object with permission checking utilities
 */
export const usePermissions = (user) => {

  const userPermissions = useMemo(() => {
    // Super users, product team, and admins have all permissions
    if (user?.isSuperUser || user?.isProductTeam || user?.isAdmin) {
      return new Set(['*']); // Wildcard represents all permissions
    }

    // Return user's permissions as a Set for fast lookups
    return new Set(user?.permissions || []);
  }, [user]);

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission key to check (e.g., 'connectors.view')
   * @returns {boolean} True if user has the permission
   */
  const hasPermission = (permission) => {
    if (!permission) return false;

    // Super users, admins, and product team have all permissions
    if (userPermissions.has('*')) return true;

    // Check for exact permission match
    return userPermissions.has(permission);
  };

  /**
   * Check if user has any of the specified permissions
   * @param {Array<string>} permissions - Array of permission keys
   * @returns {boolean} True if user has at least one permission
   */
  const hasAnyPermission = (permissions) => {
    if (!permissions || permissions.length === 0) return false;

    // Super users, admins, and product team have all permissions
    if (userPermissions.has('*')) return true;

    return permissions.some(permission => userPermissions.has(permission));
  };

  /**
   * Check if user has all of the specified permissions
   * @param {Array<string>} permissions - Array of permission keys
   * @returns {boolean} True if user has all permissions
   */
  const hasAllPermissions = (permissions) => {
    if (!permissions || permissions.length === 0) return true;

    // Super users, admins, and product team have all permissions
    if (userPermissions.has('*')) return true;

    return permissions.every(permission => userPermissions.has(permission));
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSuperUser: user?.isSuperUser || false,
    isProductTeam: user?.isProductTeam || false,
    isAdmin: user?.isAdmin || false,
  };
};
